import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

// Visual Editor "Edit text & images" for one school (migration 077,
// accounts.inline_edit_mode). Automatic = on once the school is Maintained
// with a live domain; On / Off override that. The server decides the
// effective value (functions/_shared/inline-edits.js) and returns it as
// inlineEditEnabled + inlineEditReason; this card only picks the mode and
// saves it straight away.

const MODES = [
  { value: 'auto', label: 'Automatic' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

const WAITING = 'Off: turns on automatically once the school is Maintained with a live domain';
export const INLINE_EDIT_STATUS = Object.freeze({
  auto_on: 'On automatically: Maintained with a live domain',
  auto_not_maintained: WAITING,
  auto_no_custom_domain: WAITING,
  auto_not_school: 'Off: not a school account',
  manual_on: 'On (set manually)',
  manual_off: 'Off (set manually)',
});

export default function InlineEditModeCard({ account, onChange }) {
  const [mode, setMode] = useState(account.inlineEditMode || 'auto');
  const [save, setSave] = useState({ state: 'idle', error: '' }); // idle | saving | saved | error

  useEffect(() => { setMode(account.inlineEditMode || 'auto'); }, [account.id, account.inlineEditMode]);

  const choose = async (next) => {
    if (next === mode || save.state === 'saving') return;
    const prev = mode;
    setMode(next);
    setSave({ state: 'saving', error: '' });
    try {
      const res = await api.put(`/api/admin/accounts/${account.id}`, { inlineEditMode: next });
      if (res?.account) onChange?.(res.account);
      setSave({ state: 'saved', error: '' });
    } catch (err) {
      setMode(prev);
      setSave({ state: 'error', error: err?.data?.error || err?.message || 'Could not save. Try again.' });
    }
  };

  const status = INLINE_EDIT_STATUS[account.inlineEditReason] || null;

  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5" data-testid="inline-edit-mode">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="text-[13px] font-semibold text-white" id={`inline-edit-mode-${account.id}`}>Edit text &amp; images</h3>
        <span role="status" aria-live="polite" data-testid="inline-edit-save-state" className="text-[11px]">
          {save.state === 'saving' && (
            <span className="inline-flex items-center gap-1.5 text-gray-400"><Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Saving…</span>
          )}
          {save.state === 'saved' && (
            <span className="inline-flex items-center gap-1.5 text-[#B0FC38]"><Check className="w-3 h-3" aria-hidden="true" /> Saved</span>
          )}
          {save.state === 'error' && <span className="text-red-300">{save.error}</span>}
        </span>
      </div>

      <div
        className="inline-flex rounded-lg border border-white/[0.08] bg-white/[0.02] p-0.5"
        role="group"
        aria-labelledby={`inline-edit-mode-${account.id}`}
      >
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            aria-pressed={mode === m.value}
            disabled={save.state === 'saving'}
            onClick={() => choose(m.value)}
            data-testid={`inline-edit-mode-${m.value}`}
            className={`px-3 py-1 rounded-md text-[12px] transition-colors disabled:cursor-wait ${mode === m.value ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-gray-200'}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {status && (
        <p
          data-testid="inline-edit-status"
          className={`text-[13px] mt-3 ${account.inlineEditEnabled ? 'text-[#B0FC38]' : 'text-gray-400'}`}
        >
          {status}
        </p>
      )}
      <p className="text-[12px] text-gray-500 mt-1">
        Publishing also needs the inline-publish workflow in the school’s repo; the Site readiness panel checks for it.
      </p>
    </div>
  );
}
